import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Navigate } from "react-router-dom";
import { LayoutDashboard, Users, DollarSign, Palette, Settings as Cog, Trash2, ShieldCheck, ShieldOff, Ban, UserCheck, LogIn, Plus, Download, ScrollText, Inbox, Send } from "lucide-react";

const fmt$ = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);
const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "users", label: "Users", icon: Users },
  { id: "feedback", label: "Feedback", icon: Inbox },
  { id: "revenue", label: "Revenue", icon: DollarSign },
  { id: "themes", label: "Themes", icon: Palette },
  { id: "audit", label: "Audit Log", icon: ScrollText },
  { id: "settings", label: "Settings", icon: Cog },
];

export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("overview");
  if (loading) return <div className="h-64 rounded-2xl bg-card border border-border animate-pulse" />;
  if (!user?.is_admin) return <Navigate to="/dashboard" replace />;

  return (
    <div className="space-y-6" data-testid="admin-page">
      <header className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-indigo-500/15 text-indigo-400 flex items-center justify-center"><ShieldCheck className="h-5 w-5" /></div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-indigo-400 font-semibold">Admin</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Control Center</h1>
        </div>
      </header>
      <div className="flex gap-1 overflow-x-auto" data-testid="admin-nav">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition ${tab === t.id ? "bg-indigo-500/10 text-indigo-600 dark:text-indigo-300 border border-indigo-500/20" : "text-muted-foreground hover:bg-accent"}`}>
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>
      {tab === "overview" && <Overview />}
      {tab === "users" && <UsersTab me={user} />}
      {tab === "feedback" && <FeedbackTab />}
      {tab === "revenue" && <Revenue />}
      {tab === "themes" && <Themes />}
      {tab === "audit" && <AuditLog />}
      {tab === "settings" && <SettingsTab />}
    </div>
  );
}

function Card({ children, testid }) { return <div className="p-5 rounded-2xl border border-border bg-card" data-testid={testid}>{children}</div>; }
function Stat({ label, value }) { return <Card><div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</div><div className="font-display font-mono font-extrabold text-2xl mt-1">{value}</div></Card>; }

function Overview() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/overview").then(r => setD(r.data)); }, []);
  if (!d) return <div className="h-40 rounded-2xl bg-card border border-border animate-pulse" />;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-overview">
      <Stat label="Total users" value={d.users} />
      <Stat label="Active users" value={d.active_users} />
      <Stat label="Suspended" value={d.suspended} />
      <Stat label="Households" value={d.households} />
      <Stat label="Debts tracked" value={d.debts} />
      <Stat label="Bills tracked" value={d.bills} />
      <Stat label="Income sources" value={d.incomes} />
      <Stat label="New feedback" value={d.feedback_new} />
      <Stat label="Total debt tracked" value={fmt$(d.total_debt_tracked)} />
    </div>
  );
}

function Revenue() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/revenue").then(r => setD(r.data)); }, []);
  if (!d) return <div className="h-40 rounded-2xl bg-card border border-border animate-pulse" />;
  return (
    <div className="space-y-4" data-testid="admin-revenue">
      {!d.is_live && (
        <div className="p-3 rounded-xl border border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-300 text-sm" data-testid="revenue-scaffold-note">
          No payments yet. These figures go live automatically once Stripe subscriptions are connected.
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Total revenue" value={fmt$(d.total_revenue)} />
        <Stat label="MRR (last month)" value={fmt$(d.mrr)} />
        <Stat label="Active subscriptions" value={d.active_subscriptions} />
        <Stat label="Transactions" value={d.transactions} />
      </div>
      <Card testid="revenue-by-month">
        <div className="font-display font-bold mb-3">Revenue by month</div>
        {d.by_month.length === 0 ? <div className="text-sm text-muted-foreground">Nothing to show yet.</div> : (
          <ul className="space-y-1 text-sm">{d.by_month.map(m => <li key={m.month} className="flex justify-between"><span className="text-muted-foreground">{m.month}</span><span className="font-mono font-semibold">{fmt$(m.amount)}</span></li>)}</ul>
        )}
      </Card>
    </div>
  );
}

function csv(rows) {
  if (!rows.length) return "";
  const cols = Object.keys(rows[0]);
  const esc = v => { const s = v == null ? "" : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  return [cols.join(","), ...rows.map(r => cols.map(c => esc(r[c])).join(","))].join("\n");
}
function download(name, text) { const b = new Blob([text], { type: "text/csv" }); const u = URL.createObjectURL(b); const a = document.createElement("a"); a.href = u; a.download = name; a.click(); URL.revokeObjectURL(u); }

function UsersTab({ me }) {
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const load = () => api.get(`/admin/users?q=${encodeURIComponent(q)}`).then(r => setData(r.data));
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const impersonate = async (u) => {
    if (!window.confirm(`Sign in as ${u.name} for support? You'll be switched to their account.`)) return;
    try { const { data } = await api.post(`/admin/users/${u.user_id}/impersonate`); localStorage.setItem("bf_session_token", data.session_token); toast.success(`Now viewing as ${u.name}`); setTimeout(() => window.location.href = "/dashboard", 600); }
    catch { toast.error("Could not impersonate"); }
  };

  return (
    <div className="space-y-3" data-testid="admin-users">
      <div className="flex items-center gap-2 flex-wrap">
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name or email…" data-testid="admin-user-search"
          className="flex-1 min-w-[200px] px-3 py-2 rounded-lg bg-card border border-border text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/40" />
        <button onClick={() => download("oweverit-users.csv", csv((data?.users || []).map(({ picture, ...r }) => r)))} className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border text-sm font-medium hover:bg-accent"><Download className="h-4 w-4" /> Export</button>
      </div>
      {!data ? <div className="h-40 rounded-2xl bg-card border border-border animate-pulse" /> : (
        <div className="rounded-2xl border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-accent/40 border-b border-border"><tr className="text-left text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
              <th className="p-3">User</th><th className="p-3">Debts</th><th className="p-3">Bills</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th></tr></thead>
            <tbody>
              {data.users.map(u => (
                <tr key={u.user_id} className="border-b border-border/60 hover:bg-accent/30" data-testid={`admin-user-${u.user_id}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      {u.picture ? <img src={u.picture} className="h-7 w-7 rounded-full object-cover" alt="" /> : <div className="h-7 w-7 rounded-full bg-accent flex items-center justify-center text-xs font-bold">{u.name?.[0]}</div>}
                      <div><div className="font-semibold flex items-center gap-1.5">{u.name}{u.is_admin && <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase bg-indigo-500/15 text-indigo-400 border border-indigo-500/30">Admin</span>}</div><div className="text-[11px] text-muted-foreground">{u.email}</div></div>
                    </div>
                  </td>
                  <td className="p-3 font-mono">{u.debts}</td>
                  <td className="p-3 font-mono">{u.bills}</td>
                  <td className="p-3">{u.suspended ? <span className="text-rose-500 font-semibold text-xs">Suspended</span> : <span className="text-emerald-500 text-xs">Active</span>}</td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-1">
                      {!u.env_admin && (
                        <button title={u.is_admin ? "Revoke admin" : "Make admin"} data-testid={`admin-toggle-${u.user_id}`} onClick={() => act(() => api.post(`/admin/users/${u.user_id}/admin`, { is_admin: !u.is_admin }), u.is_admin ? "Admin revoked" : "Promoted to admin")}
                          className="p-1.5 rounded-md hover:bg-accent">{u.is_admin ? <ShieldOff className="h-4 w-4" /> : <ShieldCheck className="h-4 w-4" />}</button>
                      )}
                      <button title="Impersonate" data-testid={`admin-impersonate-${u.user_id}`} onClick={() => impersonate(u)} className="p-1.5 rounded-md hover:bg-accent"><LogIn className="h-4 w-4" /></button>
                      {u.user_id !== me.user_id && (
                        <>
                          <button title={u.suspended ? "Unsuspend" : "Suspend"} data-testid={`admin-suspend-${u.user_id}`} onClick={() => act(() => api.post(`/admin/users/${u.user_id}/suspend`, { suspended: !u.suspended }), u.suspended ? "Unsuspended" : "Suspended")}
                            className="p-1.5 rounded-md hover:bg-amber-500/10 text-amber-500">{u.suspended ? <UserCheck className="h-4 w-4" /> : <Ban className="h-4 w-4" />}</button>
                          <button title="Delete" data-testid={`admin-delete-${u.user_id}`} onClick={() => { if (window.confirm(`Permanently delete ${u.name} and all their data?`)) act(() => api.delete(`/admin/users/${u.user_id}`), "User deleted"); }}
                            className="p-1.5 rounded-md hover:bg-rose-500/10 text-rose-500"><Trash2 className="h-4 w-4" /></button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const EMOJI_OPTIONS = [
  { char: "🎃", label: "Pumpkin" },
  { char: "👻", label: "Ghost" },
  { char: "🦇", label: "Bat" },
  { char: "🕷️", label: "Spider" },
  { char: "🍂", label: "Autumn leaf" },
  { char: "🦃", label: "Turkey" },
  { char: "🎄", label: "Christmas tree" },
  { char: "🎅", label: "Santa" },
  { char: "⛄", label: "Snowman" },
  { char: "❄️", label: "Snowflake" },
  { char: "🎁", label: "Gift" },
  { char: "🎉", label: "Party popper" },
  { char: "🎊", label: "Confetti" },
  { char: "🥳", label: "Celebration" },
  { char: "🎆", label: "Fireworks" },
  { char: "❤️", label: "Heart" },
  { char: "💘", label: "Cupid heart" },
  { char: "🌸", label: "Blossom" },
  { char: "🌷", label: "Tulip" },
  { char: "🐣", label: "Chick" },
  { char: "🐰", label: "Bunny" },
  { char: "☀️", label: "Sun" },
  { char: "🏖️", label: "Beach" },
  { char: "🇺🇸", label: "US flag" },
  { char: "⭐", label: "Star" },
  { char: "✨", label: "Sparkles" },
  { char: "💰", label: "Money bag" },
  { char: "🪙", label: "Coin" },
];

const emptyTheme = { name: "", accent: "#F97316", emoji: "🎃", banner: "", decoration: "none", effect: "none", auto: false, start: "", end: "" };
function Themes() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const load = () => api.get("/admin/themes").then(r => setData(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      if (form.id) await api.put(`/admin/themes/${form.id}`, form); else await api.post("/admin/themes", form);
      toast.success("Theme saved"); setForm(null); load();
    } catch { toast.error("Could not save"); }
  };
  const activate = async (t, on) => { try { await api.post(`/admin/themes/${t.id}/activate?on=${on}`); toast.success(on ? `${t.name} is live` : "Theme deactivated"); load(); } catch { toast.error("Failed"); } };
  const del = async (t) => { if (!window.confirm(`Delete "${t.name}"?`)) return; await api.delete(`/admin/themes/${t.id}`); toast.success("Deleted"); load(); };

  if (!data) return <div className="h-40 rounded-2xl bg-card border border-border animate-pulse" />;
  return (
    <div className="space-y-4" data-testid="admin-themes">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Seasonal reskins — accent colour + a greeting banner. Activate one, or let it auto-run inside a date range.</p>
        <button onClick={() => setForm({ ...emptyTheme })} data-testid="admin-theme-add" className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-indigo-500 text-white text-sm font-semibold hover:bg-indigo-600"><Plus className="h-4 w-4" /> New theme</button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {data.themes.map(t => (
          <div key={t.id} className="p-4 rounded-2xl border border-border bg-card" data-testid={`admin-theme-${t.id}`} style={{ borderColor: t.active ? t.accent : undefined }}>
            <div className="flex items-center gap-2">
              <span className="h-6 w-6 rounded-md" style={{ background: t.accent }} />
              <span className="font-display font-bold">{t.emoji} {t.name}</span>
              {t.active && <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase bg-emerald-500/15 text-emerald-500 border border-emerald-500/30">Live</span>}
              {t.auto && <span className="text-[10px] text-muted-foreground">auto {t.start}–{t.end}</span>}
            </div>
            {t.banner && <div className="text-xs text-muted-foreground mt-1">"{t.banner}"</div>}
            <div className="flex items-center gap-1 mt-3">
              <button onClick={() => activate(t, !t.active)} data-testid={`admin-theme-activate-${t.id}`} className="px-2.5 py-1 rounded-md text-xs font-semibold border border-border hover:bg-accent">{t.active ? "Deactivate" : "Activate"}</button>
              <button onClick={() => setForm({ ...t })} className="px-2.5 py-1 rounded-md text-xs font-semibold border border-border hover:bg-accent">Edit</button>
              <button onClick={() => del(t)} className="p-1.5 rounded-md hover:bg-rose-500/10 text-rose-500"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
        {data.themes.length === 0 && <div className="text-sm text-muted-foreground">No themes yet. Create one for Halloween, Christmas, etc.</div>}
      </div>

      {form && (
        <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setForm(null)}>
          <div onClick={e => e.stopPropagation()} className="w-full max-w-md bg-card border border-border rounded-2xl p-6 space-y-3" data-testid="admin-theme-form">
            <div className="font-display font-bold text-lg">{form.id ? "Edit theme" : "New theme"}</div>
            <L label="Name"><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="theme-name" className={inp} placeholder="Halloween" /></L>
            <div className="grid grid-cols-2 gap-3">
              <L label="Accent colour"><input type="color" value={form.accent} onChange={e => setForm(f => ({ ...f, accent: e.target.value }))} data-testid="theme-accent" className="w-full h-9 rounded-lg bg-background border border-border mt-1" /></L>
              <L label="Emoji"><select value={form.emoji || ""} onChange={e => setForm(f => ({ ...f, emoji: e.target.value }))} data-testid="theme-emoji" className={inp}>
                <option value="">None</option>
                {EMOJI_OPTIONS.map(e => <option key={e.char} value={e.char}>{e.char}  {e.label}</option>)}
              </select></L>
            </div>
            <L label="Banner greeting"><input value={form.banner} onChange={e => setForm(f => ({ ...f, banner: e.target.value }))} data-testid="theme-banner-text" className={inp} placeholder="Happy Halloween from OWEverit!" /></L>
            <L label="Logo decoration"><select value={form.decoration || "none"} onChange={e => setForm(f => ({ ...f, decoration: e.target.value }))} data-testid="theme-decoration" className={inp}>
              <option value="none">None</option>
              <option value="christmas">Christmas (Santa hat)</option>
              <option value="halloween">Halloween (witch hat)</option>
            </select></L>
            <L label="Ambient effect"><select value={form.effect || "none"} onChange={e => setForm(f => ({ ...f, effect: e.target.value }))} data-testid="theme-effect" className={inp}>
              <option value="none">None</option>
              <option value="snow">Snow ❄️</option>
              <option value="leaves">Falling leaves 🍂</option>
              <option value="bats">Bats 🦇</option>
              <option value="confetti">Confetti 🎉</option>
              <option value="hearts">Hearts ❤️</option>
            </select></L>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.auto} onChange={e => setForm(f => ({ ...f, auto: e.target.checked }))} data-testid="theme-auto" /> Auto-activate inside a date range</label>
            {form.auto && (
              <div className="grid grid-cols-2 gap-3">
                <L label="Start (MM-DD)"><input value={form.start || ""} onChange={e => setForm(f => ({ ...f, start: e.target.value }))} className={inp} placeholder="10-24" /></L>
                <L label="End (MM-DD)"><input value={form.end || ""} onChange={e => setForm(f => ({ ...f, end: e.target.value }))} className={inp} placeholder="11-01" /></L>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2"><button onClick={() => setForm(null)} className="px-4 py-2 rounded-lg border border-border text-sm">Cancel</button><button onClick={save} disabled={!form.name} data-testid="theme-save" className="px-4 py-2 rounded-lg bg-indigo-500 text-white text-sm font-semibold disabled:opacity-50">Save</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

function AuditLog() {
  const [logs, setLogs] = useState(null);
  useEffect(() => { api.get("/admin/audit").then(r => setLogs(r.data.logs)); }, []);
  const badge = { promote_admin: "text-indigo-400", revoke_admin: "text-muted-foreground", suspend: "text-amber-500", unsuspend: "text-emerald-500", delete_user: "text-rose-500", impersonate: "text-cyan-500" };
  if (!logs) return <div className="h-40 rounded-2xl bg-card border border-border animate-pulse" />;
  return (
    <div className="rounded-2xl border border-border bg-card overflow-x-auto" data-testid="admin-audit">
      {logs.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">No admin actions recorded yet.</div> : (
        <table className="w-full text-sm">
          <thead className="bg-accent/40 border-b border-border"><tr className="text-left text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
            <th className="p-3">When</th><th className="p-3">Admin</th><th className="p-3">Action</th><th className="p-3">Target</th></tr></thead>
          <tbody>
            {logs.map(l => (
              <tr key={l.id} className="border-b border-border/60" data-testid={`audit-row-${l.id}`}>
                <td className="p-3 text-muted-foreground whitespace-nowrap">{new Date(l.at).toLocaleString()}</td>
                <td className="p-3">{l.actor_name || l.actor_email}</td>
                <td className={`p-3 font-semibold capitalize ${badge[l.action] || ""}`}>{l.action.replace(/_/g, " ")}</td>
                <td className="p-3 text-muted-foreground">{l.target}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function SettingsTab() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/admin/config").then(r => setCfg(r.data)); }, []);
  const saveAnn = async () => { try { await api.put("/admin/config", { announcement: cfg.announcement }); toast.success("Announcement saved"); } catch { toast.error("Failed"); } };
  const saveFlags = async (flags) => { setCfg(c => ({ ...c, feature_flags: flags })); try { await api.put("/admin/config", { feature_flags: flags }); toast.success("Feature flags updated"); } catch { toast.error("Failed"); } };
  if (!cfg) return <div className="h-40 rounded-2xl bg-card border border-border animate-pulse" />;
  const ann = cfg.announcement || { enabled: false, message: "", tone: "info", start: "", end: "" };
  return (
    <div className="space-y-4" data-testid="admin-settings">
      <Card testid="admin-announcement">
        <div className="font-display font-bold text-lg">Global announcement</div>
        <p className="text-xs text-muted-foreground mt-1">Shows a banner to every signed-in user (maintenance, new features, etc.).</p>
        <label className="flex items-center gap-2 text-sm mt-3"><input type="checkbox" checked={ann.enabled} data-testid="ann-enabled" onChange={e => setCfg(c => ({ ...c, announcement: { ...ann, enabled: e.target.checked } }))} /> Show announcement</label>
        <input value={ann.message} data-testid="ann-message" onChange={e => setCfg(c => ({ ...c, announcement: { ...ann, message: e.target.value } }))} className={inp + " mt-3"} placeholder="We've shipped new payoff tools! …" />
        <div className="grid grid-cols-2 gap-3 mt-3">
          <L label="Start date (optional)"><input type="date" value={ann.start || ""} data-testid="ann-start" onChange={e => setCfg(c => ({ ...c, announcement: { ...ann, start: e.target.value } }))} className={inp} /></L>
          <L label="End date (optional)"><input type="date" value={ann.end || ""} data-testid="ann-end" onChange={e => setCfg(c => ({ ...c, announcement: { ...ann, end: e.target.value } }))} className={inp} /></L>
        </div>
        <p className="text-[11px] text-muted-foreground mt-1">Leave dates blank to show immediately and indefinitely. Outside the range it stays hidden even when enabled.</p>
        <div className="flex items-center gap-2 mt-3">
          <select value={ann.tone} data-testid="ann-tone" onChange={e => setCfg(c => ({ ...c, announcement: { ...ann, tone: e.target.value } }))} className="px-3 py-2 rounded-lg bg-background border border-border text-sm">
            <option value="info">Info</option><option value="warning">Warning</option><option value="success">Success</option>
          </select>
          <button onClick={saveAnn} data-testid="ann-save" className="px-4 py-2 rounded-lg bg-indigo-500 text-white text-sm font-semibold hover:bg-indigo-600">Save</button>
        </div>
      </Card>
      <Card testid="admin-flags">
        <div className="font-display font-bold text-lg">Feature flags</div>
        <p className="text-xs text-muted-foreground mt-1">Turn features on/off across the app without a deploy.</p>
        <ul className="mt-3 divide-y divide-border/60">
          {Object.keys(cfg.feature_flags || {}).map(k => (
            <li key={k} className="py-2.5 flex items-center justify-between">
              <span className="text-sm font-medium capitalize">{k.replace(/_/g, " ")}</span>
              <button data-testid={`flag-${k}`} onClick={() => saveFlags({ ...cfg.feature_flags, [k]: !cfg.feature_flags[k] })}
                className={`relative h-6 w-11 rounded-full transition-colors ${cfg.feature_flags[k] !== false ? "bg-emerald-500" : "bg-accent"}`}>
                <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${cfg.feature_flags[k] !== false ? "translate-x-5" : "translate-x-0.5"}`} />
              </button>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

const inp = "w-full px-3 py-2 rounded-lg bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/40";
function L({ label, children }) { return <label className="block"><span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</span>{children}</label>; }

const STATUS_META = {
  new: { label: "New", cls: "bg-indigo-500/15 text-indigo-400 border-indigo-500/30" },
  in_progress: { label: "In progress", cls: "bg-amber-500/15 text-amber-500 border-amber-500/30" },
  resolved: { label: "Resolved", cls: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30" },
};
const FILTERS = [["", "All"], ["new", "New"], ["in_progress", "In progress"], ["resolved", "Resolved"]];

function FeedbackTab() {
  const [filter, setFilter] = useState("");
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);
  const load = () => api.get(`/admin/feedback${filter ? `?status=${filter}` : ""}`).then(r => setData(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const setStatus = async (fb, status) => {
    try { await api.patch(`/admin/feedback/${fb.id}/status`, { status }); toast.success("Status updated"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-3" data-testid="admin-feedback">
      <div className="flex items-center gap-1 flex-wrap">
        {FILTERS.map(([v, lbl]) => (
          <button key={v || "all"} onClick={() => setFilter(v)} data-testid={`feedback-filter-${v || "all"}`}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${filter === v ? "bg-indigo-500/10 text-indigo-400 border-indigo-500/30" : "border-border text-muted-foreground hover:bg-accent"}`}>
            {lbl}{data?.counts && v && <span className="ml-1 opacity-70">{data.counts[v] || 0}</span>}
          </button>
        ))}
      </div>
      {!data ? <div className="h-40 rounded-2xl bg-card border border-border animate-pulse" /> : (
        data.items.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground rounded-2xl border border-border bg-card">No feedback here yet.</div> : (
          <div className="space-y-2">
            {data.items.map(fb => (
              <FeedbackItem key={fb.id} fb={fb} open={openId === fb.id} onToggle={() => setOpenId(openId === fb.id ? null : fb.id)} onStatus={setStatus} onReplied={load} />
            ))}
          </div>
        )
      )}
    </div>
  );
}

function FeedbackItem({ fb, open, onToggle, onStatus, onReplied }) {
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const meta = STATUS_META[fb.status] || STATUS_META.new;

  const send = async () => {
    const msg = reply.trim();
    if (!msg) return;
    setSending(true);
    try {
      const { data } = await api.post(`/admin/feedback/${fb.id}/reply`, { message: msg, status: fb.status === "new" ? "in_progress" : fb.status });
      toast.success(data.email_sent ? "Reply emailed to user" : "Reply saved (email unavailable)");
      setReply("");
      onReplied();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send reply"); }
    finally { setSending(false); }
  };

  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid={`feedback-item-${fb.id}`}>
      <button onClick={onToggle} className="w-full flex items-start gap-3 p-4 text-left hover:bg-accent/30 transition" data-testid={`feedback-toggle-${fb.id}`}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm">{fb.user_name || "Anonymous"}</span>
            <span className="text-[11px] text-muted-foreground">{fb.user_email}</span>
            <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase border ${meta.cls}`}>{meta.label}</span>
            {fb.replies?.length > 0 && <span className="text-[10px] text-muted-foreground">{fb.replies.length} repl{fb.replies.length === 1 ? "y" : "ies"}</span>}
          </div>
          <div className={`text-sm text-muted-foreground mt-1 ${open ? "" : "truncate"}`}>{fb.message}</div>
        </div>
        <span className="text-[10px] text-muted-foreground whitespace-nowrap">{new Date(fb.created_at).toLocaleDateString()}</span>
      </button>

      {open && (
        <div className="border-t border-border p-4 space-y-4">
          <div className="text-sm whitespace-pre-wrap">{fb.message}</div>

          {fb.replies?.length > 0 && (
            <div className="space-y-2">
              {fb.replies.map(r => (
                <div key={r.id} className="p-3 rounded-lg bg-accent/40 border border-border" data-testid={`feedback-reply-${r.id}`}>
                  <div className="flex items-center gap-2 text-[11px] text-muted-foreground mb-1">
                    <span className="font-semibold text-foreground">{r.admin_name || "Admin"}</span>
                    <span>{new Date(r.at).toLocaleString()}</span>
                    <span className={r.email_sent ? "text-emerald-500" : "text-amber-500"}>{r.email_sent ? "✓ emailed" : "not emailed"}</span>
                  </div>
                  <div className="text-sm whitespace-pre-wrap">{r.message}</div>
                </div>
              ))}
            </div>
          )}

          <div>
            <textarea value={reply} onChange={e => setReply(e.target.value)} rows={3} data-testid={`feedback-reply-input-${fb.id}`}
              placeholder="Write a reply — it will be emailed to the user…"
              className="w-full px-3 py-2.5 rounded-lg bg-background border border-border text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500/40" />
            <div className="flex items-center justify-between gap-2 mt-2">
              <div className="flex items-center gap-1">
                {Object.keys(STATUS_META).map(s => (
                  <button key={s} onClick={() => onStatus(fb, s)} data-testid={`feedback-status-${fb.id}-${s}`}
                    className={`px-2.5 py-1 rounded-md text-[11px] font-semibold border transition ${fb.status === s ? STATUS_META[s].cls : "border-border text-muted-foreground hover:bg-accent"}`}>
                    {STATUS_META[s].label}
                  </button>
                ))}
              </div>
              <button onClick={send} disabled={sending || !reply.trim()} data-testid={`feedback-send-${fb.id}`}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-500 text-white text-sm font-semibold hover:bg-indigo-600 disabled:opacity-50">
                <Send className="h-4 w-4" /> {sending ? "Sending…" : "Reply"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
